import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Shield, Smartphone, Users, Palette, BookOpen, Headphones, Sparkles } from 'lucide-react';

export const advantagesData = [
  {
    id: 1,
    title: "Proteção Anti-Pirataria",
    description: "Sistema avançado de proteção contra captura de tela, cópia e impressão não autorizada. Seus conteúdos protegidos pela Lei de Direitos Autorais.",
    image: "https://static.prod-images.emergentagent.com/jobs/cf5f99ab-41bc-43ae-9ac8-56ca61e086fd/images/0956dd7756e87a5132e09a857eb9f6827eb82b5abb166fc1fbf27547c680de50.jpeg",
    icon: Shield
  },
  {
    id: 2,
    title: "Leitura Multiplataforma",
    description: "Seus leitores acessam de qualquer dispositivo: computador, tablet ou smartphone. Experiência de leitura otimizada para cada tela.",
    image: "https://static.prod-images.emergentagent.com/jobs/cf5f99ab-41bc-43ae-9ac8-56ca61e086fd/images/8d6aa46cc0d8924bbdf2c8f51dc6a7ac6d269b7fd185549a159f5e66b06a33b6.jpeg",
    icon: Smartphone
  },
  {
    id: 3,
    title: "Gestão Completa de Assinantes",
    description: "Painel completo para gerenciar seus assinantes, acompanhar métricas de leitura e controlar acessos às suas publicações.",
    image: "https://static.prod-images.emergentagent.com/jobs/cf5f99ab-41bc-43ae-9ac8-56ca61e086fd/images/3d0ce80fea233708fb31c0164735de0fffc6706f379b33d48268aebc4c3f9590.jpeg",
    icon: Users
  },
  {
    id: 4,
    title: "Catálogo Personalizado",
    description: "Personalize seu catálogo com suas cores, logo e banner. Sua identidade visual em destaque para seus assinantes.",
    image: "https://static.prod-images.emergentagent.com/jobs/cf5f99ab-41bc-43ae-9ac8-56ca61e086fd/images/39f322bef7af3bef82e3b978debd94631928754ff7880005522973c2b2de1ba1.jpeg",
    icon: Palette
  },
  {
    id: 5,
    title: "Experiência de Leitura Premium",
    description: "Plataforma intuitiva com a melhor experiência de leitura para seus assinantes. Grifos, anotações, busca e navegação fluida em qualquer dispositivo.",
    image: "https://static.prod-images.emergentagent.com/jobs/cf5f99ab-41bc-43ae-9ac8-56ca61e086fd/images/8e13716563ddde0d11bac94c7e7cb898ce621b3430b4a9ec069130381546daf5.jpeg",
    icon: BookOpen
  },
  {
    id: 6,
    title: "Leitura Automática em Voz",
    description: "As publicações incluídas no catálogo podem ser ouvidas: o leitor ativa a leitura em voz e acompanha o texto, com controle de velocidade e pausa.",
    image: "https://images.unsplash.com/photo-1699720213476-0e63f5a4d21c?w=1920&q=80",
    icon: Headphones
  },
  {
    id: 7,
    title: "Assistente IA para Leitores",
    description: "Um assistente de inteligência artificial à disposição dos leitores, que responde perguntas sobre todo o conteúdo da publicação.",
    image: "https://static.prod-images.emergentagent.com/jobs/cf5f99ab-41bc-43ae-9ac8-56ca61e086fd/images/d32c48729ee5f5df0b0b242e1310d25e12f3b446e6dab900d132fd7ec1b825f1.jpeg",
    position: "center 35%",
    icon: Sparkles
  }
];

const AdvantagesCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % advantagesData.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + advantagesData.length) % advantagesData.length);
  }, []);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  // Autoplay
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      nextSlide();
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, nextSlide]);

  // Pause autoplay on hover
  const handleMouseEnter = () => setIsAutoPlaying(false);
  const handleMouseLeave = () => setIsAutoPlaying(true);

  return (
    <section 
      className="advantages-carousel-section"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Slides */}
      <div className="carousel-slides">
        {advantagesData.map((advantage, index) => {
          const IconComponent = advantage.icon;
          return (
            <div
              key={advantage.id}
              className={`carousel-slide ${index === currentSlide ? 'active' : ''}`}
              style={{
                backgroundImage: `url(${advantage.image})`,
                ...(advantage.position ? { backgroundPosition: advantage.position } : {}),
              }}
            >
              {/* Overlay */}
              <div className="slide-overlay"></div>
              
              {/* Content */}
              <div className="slide-content">
                <div className="slide-icon-wrapper">
                  <IconComponent className="slide-icon" />
                </div>
                <h2 className="slide-title">{advantage.title}</h2>
                <p className="slide-description">{advantage.description}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Navigation Arrows */}
      <button 
        className="carousel-arrow carousel-arrow-left"
        onClick={(e) => { e.stopPropagation(); prevSlide(); }}
        aria-label="Slide anterior"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>
      <button 
        className="carousel-arrow carousel-arrow-right"
        onClick={(e) => { e.stopPropagation(); nextSlide(); }}
        aria-label="Próximo slide"
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      {/* Dots Indicator */}
      <div className="carousel-dots">
        {advantagesData.map((_, index) => (
          <button
            key={index}
            className={`carousel-dot ${index === currentSlide ? 'active' : ''}`}
            onClick={(e) => { e.stopPropagation(); goToSlide(index); }}
            aria-label={`Ir para slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Progress Bar */}
      <div className="carousel-progress">
        <div 
          className="carousel-progress-bar"
          style={{
            width: `${((currentSlide + 1) / advantagesData.length) * 100}%`
          }}
        />
      </div>
    </section>
  );
};

export default AdvantagesCarousel;
